import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  Download,
  Filter,
  Layers,
  Plus,
  RefreshCw,
  Rows3,
  Trash2,
  X,
} from 'lucide-react';
import { JobStatusBadge, LumperName, PageHead, PriorityFlag } from '../components/bits.jsx';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Loading,
  Modal,
  Pagination,
  SearchInput,
  Segmented,
  Select,
  Switch,
  Textarea,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { JOB_STATUS, JOB_STATUS_ORDER, dateLabel, dateTimeLabel, hours, money, num, timeLabel, untilLabel } from '../lib/format.js';
import { freightPlain } from '../lib/plain.js';

const STATUS_OPTIONS = JOB_STATUS_ORDER.map((key) => ({ value: key, label: JOB_STATUS[key].label }));
const WINDOWS = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'week', label: 'Next 7 days' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All time' },
];
const SORTS = [
  { value: 'window', label: 'Window' },
  { value: 'created', label: 'Created' },
  { value: 'pay', label: 'Lumper pay' },
  { value: 'bill', label: 'Customer bill' },
  { value: 'margin', label: 'Margin' },
  { value: 'status', label: 'Status' },
  { value: 'pallets', label: 'Pallets' },
];

export default function Jobs() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') || '');
  const debouncedQuery = useDebounced(query, 300);
  const [limit, setLimit] = useState(40);
  const [offset, setOffset] = useState(0);
  const [showNew, setShowNew] = useState(params.get('new') === '1');
  const [density, setDensity] = useState('comfortable');

  const status = params.get('status') || 'all';
  const window_ = params.get('window') || 'all';
  const sort = params.get('sort') || 'window';
  const dir = params.get('dir') || (sort === 'window' ? 'asc' : 'desc');
  const unfilled = params.get('unfilled') === 'true';
  const customerId = params.get('customerId') || '';
  const lumperId = params.get('lumperId') || '';

  const filters = useMemo(
    () => ({
      status,
      window: window_,
      sort,
      dir,
      q: debouncedQuery || undefined,
      unfilled: unfilled ? 'true' : undefined,
      customerId: customerId || undefined,
      lumperId: lumperId || undefined,
      limit,
      offset,
    }),
    [status, window_, sort, dir, debouncedQuery, unfilled, customerId, lumperId, limit, offset],
  );

  const { data, meta, loading, refresh } = useApi('/jobs', { params: filters });
  const { data: customers } = useApi('/customers', { params: { limit: 200 } });
  const { data: lumpers } = useApi('/lumpers/roster');

  const jobs = data || [];
  const summary = meta?.summary || {};

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === '' || value === 'all') next.delete(key);
    else next.set(key, value);
    setOffset(0);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (params.get('new') === '1' && !showNew) setShowNew(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const activeFilters = [
    status !== 'all' && { key: 'status', label: JOB_STATUS[status]?.label || status },
    window_ !== 'all' && { key: 'window', label: WINDOWS.find((w) => w.value === window_)?.label },
    unfilled && { key: 'unfilled', label: 'Unfilled only' },
    customerId && { key: 'customerId', label: customers?.find((c) => String(c.id) === customerId)?.name || 'Customer' },
    lumperId && { key: 'lumperId', label: lumpers?.find((l) => String(l.id) === lumperId)?.firstName || 'Lumper' },
  ].filter(Boolean);

  return (
    <>
      <PageHead
        title="All jobs"
        subtitle={`${num(meta?.total ?? jobs.length)} jobs here · ${num(summary.posted || 0)} still need someone, ${num(summary.in_progress || 0)} being unloaded right now`}
        icon={ClipboardList}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Check again</Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowNew(true)}>Add a job</Button>
          </>
        }
      />

      <div className="filter-bar">
        <SearchInput
          value={query}
          onChange={(value) => {
            setQuery(value);
            setOffset(0);
          }}
          placeholder="Search by job number, customer, trailer number…"
          style={{ minWidth: 260, maxWidth: 340 }}
        />
        <Select
          className="select-sm"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
          options={[{ value: 'all', label: 'Any status' }, ...STATUS_OPTIONS]}
        />
        <Select
          className="select-sm"
          value={window_}
          onChange={(e) => setParam('window', e.target.value)}
          options={WINDOWS}
        />
        <Select
          className="select-sm"
          value={customerId}
          onChange={(e) => setParam('customerId', e.target.value)}
          options={[{ value: '', label: 'Any customer' }, ...(customers || []).map((c) => ({ value: c.id, label: c.name }))]}
        />
        <Select
          className="select-sm"
          value={lumperId}
          onChange={(e) => setParam('lumperId', e.target.value)}
          options={[
            { value: '', label: 'Any lumper' },
            ...(lumpers || []).map((l) => ({ value: l.id, label: `${l.firstName} ${l.lastName}` })),
          ]}
        />
        <Switch label="Unfilled" checked={unfilled} onChange={(e) => setParam('unfilled', e.target.checked ? 'true' : '')} />
        <div className="grow" />
        <Segmented
          options={[{ value: 'comfortable', label: 'Comfortable', icon: Rows3 }, { value: 'compact', label: 'Compact', icon: Layers }]}
          value={density}
          onChange={setDensity}
        />
        <Select
          className="select-sm"
          value={`${sort}:${dir}`}
          onChange={(e) => {
            const [nextSort, nextDir] = e.target.value.split(':');
            setParam('sort', nextSort);
            setParam('dir', nextDir);
          }}
          options={SORTS.flatMap((s) => [
            { value: `${s.value}:desc`, label: `${s.label} ↓` },
            { value: `${s.value}:asc`, label: `${s.label} ↑` },
          ])}
        />
      </div>

      {activeFilters.length ? (
        <div className="row gap-8 wrap mb-16">
          <Filter size={13} className="dim" />
          {activeFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className="chip active"
              onClick={() => setParam(filter.key, '')}
            >
              {filter.label}
              <X size={11} strokeWidth={2.6} />
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            Clear all
          </Button>
        </div>
      ) : null}

      <Card>
        {loading && !jobs.length ? (
          <Loading rows={8} />
        ) : jobs.length ? (
          <>
            <div className="table-wrap">
              <table className={cx('table', density === 'compact' && 'compact')}>
                <thead>
                  <tr>
                    <th className="narrow">Status</th>
                    <th>Job</th>
                    <th>Customer</th>
                    <th>Where</th>
                    <th>Who is doing it</th>
                    <th className="narrow">When</th>
                    <th className="right">Coming off it</th>
                    <th className="right">We pay</th>
                    <th className="right">We bill</th>
                    <th className="right">Left over</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                      <td className="narrow"><JobStatusBadge status={job.status} size="sm" /></td>
                      <td>
                        <div className="col gap-2">
                          <span className="row gap-6">
                            <span className="mono" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{job.code}</span>
                            <PriorityFlag priority={job.priority} />
                          </span>
                          <span className="dimmer truncate" style={{ fontSize: 'var(--fs-xs)' }}>
                            {job.freightType?.replace(/_/g, ' ')} · {job.trailerNumber || 'no trailer'} · {job.poNumber}
                          </span>
                        </div>
                      </td>
                      <td className="truncate">{job.customerName}</td>
                      <td>
                        <div className="col gap-2">
                          <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.siteName}</span>
                          <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{job.siteCity}, {job.siteState}</span>
                        </div>
                      </td>
                      <td>
                        <div className="col gap-4">
                          {job.lumperName ? (
                            <LumperName id={job.lumperId} name={job.lumperName} tone={job.lumperPhotoColor} />
                          ) : (
                            <Badge tone="amber" size="sm" dot>Unfilled</Badge>
                          )}
                          {Number(job.peopleNeeded) > 1 ? (
                            <Badge tone={Number(job.workerCount) >= Number(job.peopleNeeded) ? 'green' : 'amber'} size="sm">
                              Crew of {job.peopleNeeded} · {Number(job.workerCount) || 0} on it
                            </Badge>
                          ) : null}
                        </div>
                      </td>
                      <td className="narrow">
                        <div className="col gap-2">
                          <span className="num nowrap" style={{ fontSize: 'var(--fs-sm)' }}>{dateLabel(job.windowStart)} {timeLabel(job.windowStart)}</span>
                          <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{untilLabel(job.windowStart)}</span>
                        </div>
                      </td>
                      <td className="right nowrap" style={{ fontSize: 'var(--fs-sm)' }}>{freightPlain(job)}</td>
                      <td className="right num">{money(job.lumperPay)}</td>
                      <td className="right num">{money(job.customerBill)}</td>
                      <td className="right num" style={{ color: Number(job.margin) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {money(job.margin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={meta?.total || 0} limit={limit} offset={offset} onChange={setOffset} />
          </>
        ) : (
          <EmptyState
            icon={ClipboardList}
            title="No jobs match this view"
            action={<Button variant="primary" size="sm" icon={Plus} onClick={() => setShowNew(true)}>Add a job</Button>}
          >
            Try changing the filters above, or add the job you need doing.
          </EmptyState>
        )}
      </Card>

      <NewJobModal
        open={showNew}
        onClose={() => {
          setShowNew(false);
          const next = new URLSearchParams(params);
          next.delete('new');
          setParams(next, { replace: true });
        }}
        customers={customers || []}
        lumpers={lumpers || []}
        onCreated={(job) => {
          toast.ok('Job saved', 'Send it out to lumpers to fill it quickly.');
          refresh();
          navigate(`/app/jobs/${job.id}`);
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------ new order */

const emptyForm = {
  customerId: '',
  siteId: '',
  lumperId: '',
  workType: 'unload',
  freightType: 'pallets',
  commodity: '',
  temperature: 'ambient',
  priority: 'standard',
  trailerNumber: '',
  poNumber: '',
  soNumber: '',
  bolNumber: '',
  dockDoor: '',
  // Most of the work is containers, so the form asks which it is and only shows
  // the numbers that make sense for the answer.
  freightMeasure: 'containers',
  containerSize: '40ft',
  containersExpected: 1,
  palletsExpected: 24,
  estimatedHours: 3,
  lumperPay: 240,
  customerBill: 360,
  windowStart: '',
  windowEnd: '',
  instructions: '',
  notes: '',
  requirePhotos: true,
  requireSignature: false,
  broadcast: true,
};

function NewJobModal({ open, onClose, customers, lumpers, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState(emptyForm);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const siteCache = useRef(new Map());

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setError(null);
      setSites([]);
    }
  }, [open]);

  useEffect(() => {
    if (!form.customerId) {
      setSites([]);
      return undefined;
    }
    let alive = true;
    const load = async () => {
      if (siteCache.current.has(form.customerId)) {
        setSites(siteCache.current.get(form.customerId));
        return;
      }
      const payload = await api.get('/sites', { customerId: form.customerId, limit: 200 });
      siteCache.current.set(form.customerId, payload.data || []);
      if (alive) setSites(payload.data || []);
    };
    load().catch(() => {});
    return () => {
      alive = false;
    };
  }, [form.customerId]);

  useEffect(() => {
    if (!sites.length) return;
    if (!form.siteId || !sites.some((s) => String(s.id) === String(form.siteId))) {
      const first = sites[0];
      setForm((prev) => ({
        ...prev,
        siteId: String(first.id),
        instructions: prev.instructions || first.checkInInstructions || '',
        dockDoor: prev.dockDoor || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  const margin = Number(form.customerBill || 0) - Number(form.lumperPay || 0);
  const marginPct = Number(form.customerBill) ? (margin / Number(form.customerBill)) * 100 : 0;

  const submit = async () => {
    if (!form.customerId || !form.siteId || !form.windowStart) {
      setError('Customer, facility and a start time are required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = await api.post('/jobs', {
        ...form,
        customerId: Number(form.customerId),
        siteId: Number(form.siteId),
        lumperId: form.lumperId ? Number(form.lumperId) : undefined,
        // Only the numbers that belong to the answer: a container job is not
        // also "0 pallets", it simply is not measured in pallets.
        palletsExpected: form.freightMeasure === 'pallets' ? Number(form.palletsExpected) : 0,
        containersExpected: form.freightMeasure === 'containers' ? Number(form.containersExpected || 1) : 0,
        containerSize: form.freightMeasure === 'containers' ? form.containerSize : null,
        estimatedHours: Number(form.estimatedHours),
        lumperPay: Number(form.lumperPay),
        customerBill: Number(form.customerBill),
        windowStart: new Date(form.windowStart).toISOString(),
        windowEnd: form.windowEnd ? new Date(form.windowEnd).toISOString() : undefined,
        requirePhotos: form.requirePhotos,
        requireSignature: form.requireSignature,
        status: 'posted',
      });
      const job = payload.data;
      if (form.broadcast) {
        try {
          await api.post(`/dispatch/blast/${job.id}`, { top: 8 });
          toast.info('Offered to the top 8 lumpers');
        } catch {
          toast.info('Posted, but nobody was in range to offer it to');
        }
      }
      onCreated(job);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Add a job"
      subtitle="Fill in what needs doing. You can send it out to lumpers straight away."
      footer={
        <>
          <span className="grow dim" style={{ fontSize: 'var(--fs-sm)' }}>
            Left over for us <strong className="num" style={{ color: margin >= 0 ? 'var(--green)' : 'var(--red)' }}>{money(margin)}</strong>
            {Number(form.customerBill) ? ` · ${marginPct.toFixed(1)}%` : ''}
          </span>
          <Button variant="ghost" onClick={onClose}>Never mind</Button>
          <Button variant="primary" loading={busy} onClick={submit} icon={Plus}>Save this job</Button>
        </>
      }
    >
      <div className="col gap-20">
        {error ? <div className="field-error">{error}</div> : null}

        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="Which customer?" required>
            <Select
              value={form.customerId}
              placeholder="Choose a customer"
              onChange={(e) => set('customerId', e.target.value)}
              options={customers.map((c) => ({ value: c.id, label: `${c.name} · ${c.terms}` }))}
            />
          </Field>
          <Field label="Which warehouse?" required hint={sites.length ? `${sites.length} places on file` : 'Pick a customer first'}>
            <Select
              value={form.siteId}
              placeholder="Choose a facility"
              onChange={(e) => {
                const site = sites.find((s) => String(s.id) === e.target.value);
                setForm((prev) => ({
                  ...prev,
                  siteId: e.target.value,
                  instructions: site?.checkInInstructions || prev.instructions,
                }));
              }}
              options={sites.map((s) => ({ value: s.id, label: `${s.name} — ${s.city}, ${s.state}` }))}
            />
          </Field>
        </div>

        <div className="grid grid-3" style={{ gap: 14 }}>
          <Field label="What needs doing?">
            <Select
              value={form.workType}
              onChange={(e) => set('workType', e.target.value)}
              options={[
                { value: 'unload', label: 'Unload' },
                { value: 'load', label: 'Load out' },
                { value: 'sort', label: 'Sort / segregate' },
                { value: 'cross_dock', label: 'Cross dock' },
                { value: 'putaway', label: 'Putaway' },
                { value: 'rework', label: 'Rework' },
              ]}
            />
          </Field>
          <Field label="What is in the trailer?">
            <Select
              value={form.freightType}
              onChange={(e) => set('freightType', e.target.value)}
              options={[
                { value: 'pallets', label: 'Pallets' },
                { value: 'floor_loaded', label: 'Floor loaded' },
                { value: 'mixed_pallets', label: 'Mixed pallets' },
                { value: 'cases', label: 'Loose cases' },
                { value: 'produce', label: 'Produce' },
                { value: 'cross_dock', label: 'Cross dock' },
                { value: 'returns', label: 'Returns' },
                { value: 'live_load', label: 'Live load' },
              ]}
            />
          </Field>
          <Field label="What kind of trailer?">
            <Select
              value={form.temperature}
              onChange={(e) => set('temperature', e.target.value)}
              options={[
                { value: 'ambient', label: 'Dry / ambient' },
                { value: 'reefer', label: 'Reefer' },
                { value: 'frozen', label: 'Frozen' },
              ]}
            />
          </Field>
        </div>

        <div className="grid grid-3" style={{ gap: 14 }}>
          <Field label="When does it start?" required>
            <Input
              type="datetime-local"
              value={form.windowStart}
              onChange={(e) => set('windowStart', e.target.value)}
            />
          </Field>
          <Field label="When should it be done by?">
            <Input type="datetime-local" value={form.windowEnd} onChange={(e) => set('windowEnd', e.target.value)} />
          </Field>
          <Field label="How urgent is it?">
            <Select
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
              options={[
                { value: 'standard', label: 'Normal' },
                { value: 'hot', label: 'Rush — needed right away' },
                { value: 'low', label: 'Not urgent' },
              ]}
            />
          </Field>
        </div>

        <div className="grid grid-4" style={{ gap: 14 }}>
          <Field label="What is coming off it?">
            <Segmented
              options={[
                { value: 'containers', label: 'Container' },
                { value: 'pallets', label: 'Pallets' },
              ]}
              value={form.freightMeasure}
              onChange={(value) => set('freightMeasure', value)}
            />
          </Field>
          {form.freightMeasure === 'containers' ? (
            <>
              <Field label="How many containers?">
                <Input
                  type="number"
                  min="1"
                  value={form.containersExpected}
                  onChange={(e) => set('containersExpected', e.target.value)}
                />
              </Field>
              <Field label="How big?">
                <Select
                  value={form.containerSize}
                  onChange={(e) => set('containerSize', e.target.value)}
                  options={[
                    { value: '20ft', label: '20ft' },
                    { value: '40ft', label: '40ft' },
                    { value: '40ft high cube', label: '40ft high cube' },
                    { value: '53ft', label: '53ft trailer' },
                  ]}
                />
              </Field>
            </>
          ) : (
            <Field label="How many pallets?">
              <Input type="number" min="0" value={form.palletsExpected} onChange={(e) => set('palletsExpected', e.target.value)} />
            </Field>
          )}
          <Field label="How long will it take?" hint="In hours — half hours are fine">
            <Input type="number" min="0" step="0.5" value={form.estimatedHours} onChange={(e) => set('estimatedHours', e.target.value)} />
          </Field>
          <Field label="What we pay the lumper" hint="What they take home">
            <Input className="mono" type="number" min="0" value={form.lumperPay} onChange={(e) => set('lumperPay', e.target.value)} />
          </Field>
          <Field label="What we bill the customer" hint="What they pay us">
            <Input className="mono" type="number" min="0" value={form.customerBill} onChange={(e) => set('customerBill', e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-4" style={{ gap: 14 }}>
          <Field label="Trailer number">
            <Input value={form.trailerNumber} onChange={(e) => set('trailerNumber', e.target.value)} placeholder="TRL123456" />
          </Field>
          <Field label="PO number">
            <Input value={form.poNumber} onChange={(e) => set('poNumber', e.target.value)} placeholder="PO-123456" />
          </Field>
          <Field label="Order number">
            <Input value={form.soNumber} onChange={(e) => set('soNumber', e.target.value)} placeholder="SO-123456" />
          </Field>
          <Field label="Door number">
            <Input value={form.dockDoor} onChange={(e) => set('dockDoor', e.target.value)} placeholder="14" />
          </Field>
        </div>

        <Field label="What is inside?">
          <Input value={form.commodity} onChange={(e) => set('commodity', e.target.value)} placeholder="Dry grocery pallets" />
        </Field>

        <Field label="Anything the lumper should know?">
          <Textarea value={form.instructions} onChange={(e) => set('instructions', e.target.value)} rows={2} />
        </Field>

        <Field label="Our own notes">
          <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="Notes only we can see" />
        </Field>

        <div className="row gap-20 wrap">
          <Checkbox label="Ask for a photo of the finished work" checked={form.requirePhotos} onChange={(e) => set('requirePhotos', e.target.checked)} />
          <Checkbox label="Ask for the paperwork to be signed" checked={form.requireSignature} onChange={(e) => set('requireSignature', e.target.checked)} />
          <Checkbox label="Send it out to lumpers straight away" checked={form.broadcast} onChange={(e) => set('broadcast', e.target.checked)} />
        </div>
      </div>
    </Modal>
  );
}
